/** Simple inline-svg icon set. Matches
 *  design-handoff/project/components.jsx:Icon. Adds new icons by extending
 *  PATHS; everything is hand-traced on a 16×16 grid.
 */
const PATHS: Record<string, string> = {
  plus: "M8 3v10M3 8h10",
  check: "M3 8.5l3.2 3.2L13 5",
  chev: "M5 6l3 3 3-3",
  chevR: "M6 4l3 3-3 3",
  chevL: "M9 4l-3 3 3 3",
  download: "M8 2v8m0 0l-3-3m3 3l3-3M3 13h10",
  close: "M4 4l8 8M12 4l-8 8",
  play: "M5 3v10l8-5z",
  pause: "M5 3h2v10H5zM9 3h2v10H9z",
  mic: "M8 2a2 2 0 012 2v4a2 2 0 11-4 0V4a2 2 0 012-2zM4 8a4 4 0 008 0M8 12v2",
  mute: "M8 2a2 2 0 012 2v4a2 2 0 11-4 0V4a2 2 0 012-2zM4 8a4 4 0 008 0M8 12v2M2 2l12 12",
  copy: "M5 5V3a1 1 0 011-1h7a1 1 0 011 1v7a1 1 0 01-1 1h-2M3 5h7a1 1 0 011 1v7a1 1 0 01-1 1H3a1 1 0 01-1-1V6a1 1 0 011-1z",
  link: "M7 9a3 3 0 004 0l2-2a3 3 0 00-4-4l-1 1M9 7a3 3 0 00-4 0L3 9a3 3 0 004 4l1-1",
  search: "M11 11l3 3M7 12a5 5 0 100-10 5 5 0 000 10z",
  user: "M8 9a3 3 0 100-6 3 3 0 000 6zM2 15c0-3 3-5 6-5s6 2 6 5",
  bot: "M5 6h6a2 2 0 012 2v3a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2zM6 9.5h.01M10 9.5h.01M8 4V2",
  spark: "M8 2l1.5 4.5L14 8l-4.5 1.5L8 14l-1.5-4.5L2 8l4.5-1.5z",
  arrowR: "M3 8h10m0 0l-4-4m4 4l-4 4",
  arrowL: "M13 8H3m0 0l4-4m-4 4l4 4",
  file: "M4 2h5l3 3v9a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z",
  folder: "M2 5a1 1 0 011-1h3l1.5 1.5H13a1 1 0 011 1V12a1 1 0 01-1 1H3a1 1 0 01-1-1V5z",
  eye: "M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5zM8 10.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z",
  cog: "M8 5.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z M8 1v2M8 13v2M3.5 3.5l1.4 1.4M11.1 11.1l1.4 1.4M1 8h2M13 8h2M3.5 12.5l1.4-1.4M11.1 4.9l1.4-1.4",
  grid: "M2 2h5v5H2zM9 2h5v5H9zM2 9h5v5H2zM9 9h5v5H9z",
  list: "M2 4h12M2 8h12M2 12h12",
  record: "M8 2a6 6 0 100 12A6 6 0 008 2z",
  chat: "M2 4a1 1 0 011-1h10a1 1 0 011 1v6a1 1 0 01-1 1H6l-3 3v-3H3a1 1 0 01-1-1V4z",
  speaker: "M9 3L5 6H2v4h3l4 3V3zM12 6c1 1 1 3 0 4M14 4c2 2 2 6 0 8",
  transcript: "M3 2h7l3 3v9H3V2zM5 6h6M5 9h6M5 12h4",
  frames: "M2 3h12v10H2zM2 6h12M5 3v10M11 3v10M2 9.5h12",
  clock: "M8 2a6 6 0 100 12 6 6 0 000-12zM8 5v3l2 2",
  cloud: "M4 11a3 3 0 010-6 4 4 0 017 1 3 3 0 010 6H4z",
  eyeOff: "M3 3l10 10M6 6c-1.5 1-3 2-5 2 0 0 2.5 5 7 5 1.5 0 2.8-.5 4-1.3M11 11.5c1.7-1 3-2.5 4-3.5 0 0-2.5-5-7-5-.8 0-1.5.2-2.2.4",
  upvote: "M8 3l4 5h-3v5H7V8H4l4-5z",
  qa: "M8 4a2 2 0 11.001 4 2 2 0 010-4zM8 10v.01",
  key: "M10 6a3 3 0 100 4 3 3 0 000-4zM12.5 5L14 6.5M9.5 8L4 13.5l1.5 1.5L7 13l1 1 1.5-1.5",
};

export type IconName = keyof typeof PATHS;

export function Icon({
  name,
  size = 16,
  color = "currentColor",
}: {
  name: IconName | string;
  size?: number;
  color?: string;
}) {
  const d = PATHS[name as IconName] ?? PATHS.spark;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}
